import React, { useState } from "react";
import ConsultationForm from "./Consultation"; // adjust path if your pages folder is elsewhere
import IcuSuit1 from "../assets/images/Icu-Suit/Icu-Suit-1.png";
import IcuSuit2 from "../assets/images/Icu-Suit/Icu-Suit-2.jpeg";
import IcuSuit3 from "../assets/images/Icu-Suit/Icu-Suit-3.jpg";
import IcuSuit4 from "../assets/images/Icu-Suit/Icu-Suit-4.jpg";
import IcuSuit5 from "../assets/images/Icu-Suit/Icu-Suit-5.jpg";
import IcuSuit6 from "../assets/images/Icu-Suit/Icu-Suit-6.webp";
import IcuSuit7 from "../assets/images/Icu-Suit/Icu-Suit-7.jpg";
import IcuSuit8 from "../assets/images/Icu-Suit/Icu-Suit-8.jpg";
import IcuSuit9 from "../assets/images/Icu-Suit/Icu-Suit-9.jpg";
import SEO from "../components/SEO";

/**
 * ICU Layout Designs — landing page
 * Same design system as OTSuiteDesigns.jsx — black/white/grey/ash palette,
 * spec-line cards (Zone / Size / Monitoring),
 * "Get Free Quote" opens the shared Consultation modal.
 */

const FONT_IMPORT_URL =
  "https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&family=Inter:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap";

const tokens = {
  ink: "#343F86",
  bg: "#ECEEF6",
  card: "#FFFFFF",
  teal: "#3A4060",
  tealDark: "#262E63",
  amber: "#8A8F9C",
  blue: "#5B6072",
  hairline: "#CDD1E0",
  sub: "#5B6072",
};

const icuBays = [
  {
    name: "Modular Cardiac ICU Bay",
    zone: "Level 3",
    size: "16 sq.m",
    monitor: "Bedside + Central",
    tag: "Cardiac",
    note: "Dedicated cardiac monitor with arrhythmia alarms, ceiling-mounted pendant, isolation glazing.",
    image: IcuSuit1,
  },
  {
    name: "Neonatal ICU (NICU) Pod",
    zone: "Level 3",
    size: "12 sq.m",
    monitor: "Incubator + SpO2",
    tag: "NICU",
    note: "Warm-tone low-glare lighting, sound-dampened panels, dedicated isolette transfer bay.",
    image: IcuSuit2,
  },
  {
    name: "Surgical ICU Bay with Ventilator Dock",
    zone: "Level 3",
    size: "18 sq.m",
    monitor: "Multi-parameter",
    tag: "Surgical",
    note: "Integrated ventilator boom, medical gas manifold, dual IV pump rail.",
    image: IcuSuit3,
  },
  {
    name: "Isolation ICU Bay with Negative Pressure",
    zone: "Level 3",
    size: "20 sq.m",
    monitor: "Remote + Bedside",
    tag: "Isolation",
    note: "Anteroom with pressure cascade, HEPA exhaust, dedicated donning/doffing zone.",
    image: IcuSuit4,
  },
  {
    name: "Pediatric ICU (PICU) Bay",
    zone: "Level 2–3",
    size: "14 sq.m",
    monitor: "Bedside + Parent Display",
    tag: "Pediatric",
    note: "Family-zone seating, child-friendly finishes, low-noise alarm profile.",
    image: IcuSuit5,
  },
  {
    name: "Multi-Bed Open ICU Ward",
    zone: "Level 2",
    size: "80 sq.m / 6 beds",
    monitor: "Central Nursing Station",
    tag: "Multi-Bed",
    note: "Central nurse station with sightlines to all bays, shared crash-cart bay, glass partitions.",
    image: IcuSuit6,
  },
  {
    name: "Neuro ICU Bay with EEG Monitoring",
    zone: "Level 3",
    size: "18 sq.m",
    monitor: "EEG + ICP",
    tag: "Neuro",
    note: "Dimmable circadian lighting, dedicated EEG cabling conduit, vibration-isolated flooring.",
    image: IcuSuit7,
  },
  {
    name: "Step-Down HDU Bay",
    zone: "Level 1–2",
    size: "12 sq.m",
    monitor: "Telemetry",
    tag: "Step-Down",
    note: "Transitional care setup between ICU and ward, telemetry monitoring, mobile workstation dock.",
    image: IcuSuit8,
  },
  {
    name: "ICU-OT Combine Trauma Bay",
    zone: "Level 3",
    size: "22 sq.m",
    monitor: "Multi-parameter",
    tag: "Trauma",
    note: "Direct corridor link to OT, rapid-access resuscitation trolley, redundant medical gas points.",
    image: IcuSuit9,
  },
];

function SpecLine({ zone, size, monitor }) {
  return (
    <div
      style={{
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 11.5,
        letterSpacing: 0.3,
        color: tokens.sub,
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
      }}
    >
      <span>ZONE&nbsp;<b style={{ color: tokens.teal }}>{zone}</b></span>
      <span>·</span>
      <span>SIZE&nbsp;<b style={{ color: tokens.ink }}>{size}</b></span>
      <span>·</span>
      <span>{monitor}</span>
    </div>
  );
}

function ICUCard({ b, onGetQuote }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "relative",
        background: tokens.card,
        borderRadius: 4,
        overflow: "hidden",
        border: `1px solid ${tokens.hairline}`,
        boxShadow: hover
          ? "0 14px 28px -14px rgba(14,124,123,0.35)"
          : "0 2px 6px -2px rgba(43,58,66,0.08)",
        transform: hover ? "translateY(-3px)" : "none",
        transition: "all 0.25s ease",
      }}
    >
      {/* corner bracket signature */}
      <div
        style={{
          position: "absolute", top: 10, left: 10, width: 22, height: 22,
          borderTop: `2px solid ${tokens.teal}`, borderLeft: `2px solid ${tokens.teal}`,
          zIndex: 2,
        }}
      />
      <div style={{ height: 168, position: "relative", background: tokens.tealDark }}>
        {b.image ? (
          <img
            src={b.image}
            alt={b.name}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          <div
            style={{
              width: "100%", height: "100%", display: "flex",
              alignItems: "center", justifyContent: "center",
              color: "rgba(255,255,255,0.55)",
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, letterSpacing: 0.5,
            }}
          >
            ADD IMAGE
          </div>
        )}
        {/* tag chip */}
        <span
          style={{
            position: "absolute", bottom: 10, right: 12,
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
            color: "#fff", background: "rgba(0,0,0,0.45)",
            padding: "2px 8px", borderRadius: 2,
          }}
        >
          {b.tag.toUpperCase()}
        </span>
      </div>
      <div style={{ padding: "16px 16px 18px" }}>
        <h3
          style={{
            fontFamily: "'Montserrat', 'Inter', sans-serif", fontWeight: 600, fontSize: 17.5,
            color: tokens.ink, margin: "0 0 6px", lineHeight: 1.25,
          }}
        >
          {b.name}
        </h3>
        <p style={{ fontSize: 13, color: tokens.sub, margin: "0 0 10px", lineHeight: 1.45 }}>
          {b.note}
        </p>
        <SpecLine zone={b.zone} size={b.size} monitor={b.monitor} />
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button
            onClick={() => onGetQuote(b)}
            style={{
              flex: 1, padding: "9px 10px", fontSize: 12.5, fontWeight: 600,
              fontFamily: "'Montserrat', 'Inter', sans-serif", letterSpacing: 0.2,
              background: tokens.amber, color: tokens.ink,
              border: "none", borderRadius: 3, cursor: "pointer",
            }}
          >
            Get Free Quote
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ICULayoutDesigns() {
  const [showConsultation, setShowConsultation] = useState(false);
  const [selectedBay, setSelectedBay] = useState(null);

  const handleGetQuote = (bay) => {
    setSelectedBay(bay);
    setShowConsultation(true);
  };

  return (
     <>
    <SEO/>
    <div
      style={{
        background: tokens.bg,
        color: tokens.ink,
        fontFamily: "'Inter', 'Montserrat', sans-serif",
        minHeight: "100vh",
      }}
    >
      <style>{`@import url('${FONT_IMPORT_URL}');
        * { box-sizing: border-box; }
        @media (prefers-reduced-motion: reduce) { * { transition: none !important; } }
        button:focus-visible, a:focus-visible { outline: 2px solid ${tokens.teal}; outline-offset: 2px; }
        .icu-grid-3x3 { grid-template-columns: repeat(3, 1fr); }
        @media (max-width: 900px) { .icu-grid-3x3 { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 600px) { .icu-grid-3x3 { grid-template-columns: 1fr; } }
        .icu-modal-overlay {
          position: fixed; inset: 0; z-index: 2000;
          background: rgba(0, 0, 0, 0.65);
          backdrop-filter: blur(5px);
          display: flex; align-items: center; justify-content: center;
          padding: 1rem;
        }
        .icu-modal-inner {
          position: relative; width: 100%; max-width: 680px;
          max-height: 90vh; overflow-y: auto; border-radius: 18px;
        }
        .icu-modal-close {
          position: absolute; top: 14px; right: 16px; z-index: 10;
          background: #262E63; border: none; color: #fff;
          width: 32px; height: 32px; border-radius: 50%;
          font-size: 20px; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          line-height: 1;
        }
      `}</style>

      {/* spacer so the fixed/sticky page navbar in the host app doesn't overlap the grid */}
      <div style={{ height: 32 }} />

      {/* GRID */}
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "26px 24px 10px" }}>
        <div
          className="icu-grid-3x3"
          style={{
            display: "grid",
            gap: 22,
            marginTop: 70,
          }}
        >
          {icuBays.map((b, i) => (
            <ICUCard key={i} b={b} onGetQuote={handleGetQuote} />
          ))}
        </div>
      </section>

      {/* Consultation Modal — opens on "Get Free Quote" click */}
      {showConsultation && (
        <div
          className="icu-modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setShowConsultation(false); }}
        >
          <div className="icu-modal-inner">
            <button
              className="icu-modal-close"
              onClick={() => setShowConsultation(false)}
              aria-label="Close consultation form"
            >
              ×
            </button>
            <ConsultationForm suite={selectedBay} />
          </div>
        </div>
      )}
    </div>
    </>
  );
}